import { useEffect, useMemo, useRef } from 'react';
import { DatePicker } from 'antd';
import { useIntl } from 'react-intl';
import classnames from 'classnames';

import { isUserAuthorized } from 'utils/helpers';
import { ACL_MATRIX } from 'common/permissions';
import { exportBusinessAnalyticsInfo } from 'services/business';

import BRButton from 'components/BRButton/BRButton';
import { notify } from 'components/Notify/Notify';

import { ReactComponent as Download } from 'assets/bosta-icons/Download.svg';
import { ReactComponent as Shop } from 'assets/bosta-icons/Shop.svg';
import { ReactComponent as CircleCheck } from 'assets/bosta-icons/Circle-Check.svg';
import { ReactComponent as CircleFalse } from 'assets/bosta-icons/Circle-clear.svg';
import { ReactComponent as CopyIcon } from 'assets/bosta-icons/copy-06.svg';

import './BusinessHeader.less';

const BusinessHeader = ({
  businessData,
  dateValue,
  setIsLoading,
  ...props
}) => {
  const { formatMessage } = useIntl();
  const datePickerRef = useRef();

  const {
    _id: businessId,
    name,
    industry,
    businessTier,
    pricingTier,
    insurancePlan,
    status,
    isValid,
    isFirstTimeExceptionApplied
  } = businessData || {};

  useEffect(() => {
    datePickerRef.current?.focus?.();
  });

  const handleExportBusinessAnalytics = async () => {
    if (!businessId || !dateValue) return;

    setIsLoading(true);

    try {
      const { data } = await exportBusinessAnalyticsInfo({
        businessId,
        date: dateValue.format('YYYY-MM-DD')
      });

      notify(data, 'success');
    } catch (error) {
      notify(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCopyId = async () => {
    try {
      await navigator.clipboard.writeText(businessId);
      notify(
        formatMessage(
          { id: 'common.copy_success' },
          {
            label: formatMessage({
              id: 'merchant_analysis.business_id'
            })
          }
        ),
        'success'
      );
    } catch (error) {
      notify(formatMessage({ id: 'common.copy_fail' }));
    }
  };

  const headerDetails = [
    { value: `${businessTier} - ${pricingTier?.name}` },
    {
      label: formatMessage({ id: 'merchant_analysis.industry' }),
      value: industry
    },
    {
      label: formatMessage({ id: 'merchant_analysis.insurance' }),
      value: insurancePlan?.name
    }
  ];

  const businessStatus = useMemo(() => {
    if (isValid) {
      return (
        <div className="br-business-header__left-info-title-status-true">
          <CircleCheck className="br-business-header__left-info-check-icon" />
          <span className="body">{status}</span>
        </div>
      );
    } else {
      return (
        <div className="br-business-header__left-info-title-status-false">
          <CircleFalse className="br-business-header__left-info-check-icon" />
          <span className="body">{status}</span>
        </div>
      );
    }
  }, [businessData]);

  return (
    <div className="br-business-header">
      {businessData && (
        <>
          <div className="br-business-header__left">
            <span className="br-business-header__icon">
              <Shop />
            </span>
            <div className="br-business-header__left-info">
              <div className="br-business-header__left-info-title">
                <BRButton
                  type={'link-color'}
                  className={'button-sm'}
                  label={<span className="display-xs">{name}</span>}
                  onClick={() =>
                    window.open(`businesses/${businessId}/details`, '_blank')
                  }
                />
                <BRButton
                  type={'treitary-color'}
                  className={'button-sm'}
                  suffixIcon={
                    <CopyIcon className="br-business-header__copy-icon" />
                  }
                  label={formatMessage(
                    {
                      id: 'common.copy'
                    },
                    {
                      label: formatMessage({ id: 'merchant_analysis.id' })
                    }
                  )}
                  onClick={handleCopyId}
                />

                {businessStatus}
              </div>

              <div className="br-business-header__left-info-subtitle">
                <div className="br-business-header__left-info-subtitle-details">
                  {headerDetails
                    .filter(({ value }) => value)
                    .map(({ label, value }, index) => (
                      <span key={index} className="body-medium">
                        {label ? `${label} ${value}` : value}
                        {index < headerDetails.length - 1 && ' | '}
                      </span>
                    ))}
                </div>
                {!!isFirstTimeExceptionApplied && (
                  <span className="br-business-header__left-info-subtitle-first-exception caption">
                    {formatMessage({
                      id: 'merchant_analysis.first_time_exception'
                    })}
                  </span>
                )}
              </div>
            </div>
          </div>

          <div className="br-business-header__actions">
            {isUserAuthorized([], [ACL_MATRIX.BUSINESS_ANALYTICS_EXPORT]) && (
              <BRButton
                type={'basic'}
                onClick={handleExportBusinessAnalytics}
                label={formatMessage({ id: 'common.export_page' })}
                prefixIcon={<Download />}
                disabled={!businessId || !dateValue}
              />
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default BusinessHeader;
